import { useContext } from "react";
import type { CSSProperties } from "react";
import {
  createDateFormatter,
  formatPeriodDateRange,
  useDisplayTimezone,
  type TimezoneMode,
} from "@nexus/ui";
import {
  defineConfigFragment,
  defineLabels,
  defineWidgetSettings,
  firstError,
  resolveLabels,
  unwrap,
  MetricValue,
  Widget,
  WidgetEditorContext,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type {
  JsonValue,
  WidgetConfigOverlay,
  WidgetConfigProperty,
  WidgetManifest,
  WidgetModule,
  WidgetProps,
  WidgetSettings,
} from "@nexus/widget-sdk";
import {
  REPORT_METRICS_CONTRACT,
  type ReportMetric,
  type ReportMetricsData,
} from "../contracts/report-metrics";
import { useAnimatedNumber } from "../motion/useAnimatedNumber";
import { motionClass, useWidgetMotion } from "../motion/useWidgetMotion";
import { ANIMATE_VALUE_CONFIG } from "../settings/fragments";

/**
 * The metric indicator: one figure, two side by side, or a primary figure above
 * two supporting figures, with captions and comparison trend. Every domain indicator
 * (`ip-blocking-indicator`, `piracy-audience-indicator`…) and the generic
 * `metric-kpi` are this component on a different source; see {@link createIndicator}.
 */

/** Which direction of change is good news; `neutral` never colors the trend. */
export type TrendPolarity = "higher-is-better" | "lower-is-better" | "neutral";

export interface MetricIndicatorConfig {
  /** Heading, with the host's title variables. Starts on the widget name; empty falls back to it. */
  title?: string;
  metricId?: string;
  /** Caption below the value. Defaults to the metric's label when empty. */
  label?: string;
  /** A second metric of the same collection, shown beside the first. */
  secondaryMetricId?: string;
  /** Caption below the second value. Defaults to that metric's label. */
  secondaryLabel?: string;
  thirdMetricId?: string;
  thirdLabel?: string;
  /** Show the change against the metric's comparison period, when the data has one. */
  showTrend?: boolean;
  animateValue?: boolean;
  trendPolarity?: TrendPolarity;
  /** Per-instance overrides of the texts listed in `manifest.labels`. */
  labels?: Record<string, string>;
}

// Missing tenants, a source without data and failures use the shared issue state
// (WidgetIssueState), the same in every widget: they have no indicator-specific text.
export const INDICATOR_LABELS = defineLabels([
  {
    key: "metricUnavailable",
    name: "Metric missing from the source",
    default: "Metric unavailable",
  },
  {
    key: "notAvailable",
    name: "No value for the period",
    default: "Not available for this period",
  },
] as const);

const compactFormat = (digits: number) =>
  new Intl.NumberFormat("en", { maximumFractionDigits: digits });

export function formatMetricValue(metric: ReportMetric): string {
  if (metric.value === null || !Number.isFinite(metric.value)) return "—";
  if (metric.format === "percent") return `${compactFormat(0).format(metric.value * 100)}%`;
  const formatted = compactFormat(metric.format === "decimal" ? 1 : 0).format(metric.value);
  return metric.unit ? `${formatted} ${metric.unit}` : formatted;
}

export function formatMetricOccurrence(
  metric: ReportMetric,
  timezone: TimezoneMode = "utc",
): string | undefined {
  if (metric.value === null || !Number.isFinite(metric.value) || !metric.occurredAt) {
    return undefined;
  }
  const timestamp = Date.parse(metric.occurredAt);
  return Number.isFinite(timestamp)
    ? createDateFormatter(timezone, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(timestamp)
    : undefined;
}

/**
 * "3 of 7 days" when the value is computed from part of its period only;
 * nothing when complete, unknown, or the period is shorter than two days.
 */
export function formatMetricCoverage(metric: ReportMetric): string | undefined {
  const { coverage, period } = metric;
  if (metric.value === null || coverage == null || !(coverage > 0 && coverage < 1) || !period)
    return undefined;
  const days = (Date.parse(period.to) - Date.parse(period.from)) / 86_400_000;
  if (!Number.isFinite(days) || days < 2) return undefined;
  const total = Math.round(days);
  const covered = Math.max(1, Math.round(coverage * days));
  return covered < total ? `${covered} of ${total} days` : undefined;
}

export function formatMetricPeriod(
  period: ReportMetric["period"],
  timezone: TimezoneMode = "utc",
): string | undefined {
  if (!period) return undefined;
  if (period.key !== "dashboard" && period.key !== "selected") return period.label;
  return formatPeriodDateRange(period.from, period.to, timezone) ?? period.label;
}

export type TrendDirection = "up" | "down" | "neutral";

export interface MetricTrend {
  direction: TrendDirection;
  /** Signed percentage points of change, e.g. "+11.1%". */
  text: string;
  /** Spoken equivalent; the arrow and color are never the only signal. */
  description: string;
}

/**
 * The comparison change of a metric, taken from the data only. Returns `null`
 * when the source supplied no usable comparison, so the tile shows nothing
 * rather than an invented "0 %".
 */
export function getMetricTrend(metric: ReportMetric): MetricTrend | null {
  if (metric.value === null || !Number.isFinite(metric.value)) return null;
  let delta = metric.deltaPercent ?? null;
  if (
    delta === null &&
    metric.value !== null &&
    metric.comparisonValue != null &&
    metric.comparisonValue !== 0
  ) {
    delta = ((metric.value - metric.comparisonValue) / Math.abs(metric.comparisonValue)) * 100;
  }
  if (delta === null || !Number.isFinite(delta)) return null;
  const rounded = (Math.sign(delta) * Math.round(Math.abs(delta) * 10)) / 10;
  const direction: TrendDirection = rounded > 0 ? "up" : rounded < 0 ? "down" : "neutral";
  const magnitude = compactFormat(1).format(Math.abs(rounded));
  return {
    direction,
    text: `${rounded > 0 ? "+" : rounded < 0 ? "−" : ""}${magnitude}%`,
    description:
      direction === "neutral"
        ? "Trend: unchanged versus the previous period"
        : `Trend: ${direction} ${magnitude}% versus the previous period`,
  };
}

const TREND_ARROW: Record<TrendDirection, string> = { up: "▲", down: "▼", neutral: "■" };

/** How long a figure takes to count up or roll to a new value. */
const COUNT_MS = 1100;

function trendClassName(direction: TrendDirection, polarity: TrendPolarity): string {
  if (direction === "neutral" || polarity === "neutral") {
    return "border border-border bg-surface-elevated text-secondary";
  }
  const good = (direction === "up") === (polarity === "higher-is-better");
  return good ? "bg-status-ok/10 text-status-ok-text" : "bg-status-error/10 text-status-error-text";
}

/**
 * A change against the previous period: an arrow and a signed percentage on a
 * pill, green when it goes the good way for the figure (`polarity`), red when
 * not, neutral otherwise. Shared by the indicators and the tiles that compare.
 */
export function TrendBadge({
  trend,
  polarity,
  motion,
}: {
  trend: MetricTrend;
  polarity: TrendPolarity;
  motion: boolean;
}) {
  return (
    <span
      // Settles in once the figure has mostly counted up: the change qualifies the value.
      style={{ "--nx-delay": `${Math.round(COUNT_MS * 0.55)}ms` } as CSSProperties}
      className={`${motionClass(motion, "nx-pop")} shrink-0 rounded px-2 py-1 text-metric-detail font-semibold tabular-nums ${trendClassName(trend.direction, polarity)}`}
      aria-label={trend.description}
      title={trend.description}
    >
      <span aria-hidden>
        {TREND_ARROW[trend.direction]} {trend.text}
      </span>
    </span>
  );
}

/** What one figure of the indicator shows, whatever the state of its data. */
function useFigure(
  data: WidgetProps<MetricIndicatorConfig>["data"],
  metricId: string | undefined,
  fallbackToFirst: boolean,
  animateValue: boolean,
) {
  const error = firstError(data, ["metrics"]);
  const result = unwrap<ReportMetricsData>(data.metrics);
  const chosenId = metricId?.trim();
  const metric = chosenId
    ? result?.metrics.find((entry) => entry.id === chosenId)
    : fallbackToFirst
      ? result?.metrics[0]
      : undefined;
  const loading = Boolean(data.metrics?.isLoading && !result);
  const motion = useWidgetMotion();
  const shown = useAnimatedNumber(error ? null : metric?.value, {
    duration: COUNT_MS,
    enabled: motion && animateValue,
  });
  return { error, metric, loading, motion, shown };
}

function Figure({
  figure,
  caption,
  config,
  inEditor,
  labels,
  layout,
  periodDetail = false,
  showTrend = config.showTrend,
  supporting = false,
}: {
  figure: ReturnType<typeof useFigure>;
  caption: string | undefined;
  config: MetricIndicatorConfig;
  inEditor: boolean;
  labels: ReturnType<typeof resolveLabels<typeof INDICATOR_LABELS>>;
  /** `single`: caption and trend share a row; `pair`: they stack under the value. */
  layout: "single" | "pair";
  /** Under the caption, the period the figure covers when it names no moment of its own. */
  periodDetail?: boolean;
  showTrend?: boolean;
  supporting?: boolean;
}) {
  const { metric, loading, motion, shown } = figure;
  const timezone = useDisplayTimezone();
  const trend = showTrend && metric ? getMetricTrend(metric) : null;
  const unavailable =
    !loading && (!metric || metric.value === null || !Number.isFinite(metric.value));
  const display = metric
    ? formatMetricValue({ ...metric, value: shown ?? metric.value })
    : undefined;
  const occurrence = !loading && metric ? formatMetricOccurrence(metric, timezone) : undefined;
  const coverage = !loading && metric ? formatMetricCoverage(metric) : undefined;
  const period =
    periodDetail && !occurrence && !loading && !unavailable && metric
      ? formatMetricPeriod(metric.period, timezone)
      : undefined;
  const text =
    !loading && !metric
      ? inEditor
        ? (caption?.trim() ?? " ")
        : labels.metricUnavailable
      : unavailable
        ? labels.notAvailable
        : caption?.trim() || metric?.label || "Metric";
  const captionNode = (
    <div
      key={metric ? "ready" : "pending"}
      className={`${motionClass(motion, "nx-rise")} min-w-0`}
      style={{ "--nx-delay": "120ms" } as CSSProperties}
    >
      <p className={`${supporting ? "text-metric-caption" : "text-metric-label"} text-secondary`}>
        {text}
      </p>
      {occurrence && (
        <time
          dateTime={metric?.occurredAt ?? undefined}
          className="mt-1 block break-words text-metric-detail text-secondary tabular-nums"
        >
          {occurrence}
        </time>
      )}
      {period && (
        <span className="mt-1 block break-words text-metric-detail text-muted">{period}</span>
      )}
      {coverage && (
        <span className="mt-1 block break-words text-metric-detail text-muted tabular-nums">
          {coverage}
        </span>
      )}
    </div>
  );
  const badge =
    trend && metric ? (
      <TrendBadge trend={trend} polarity={config.trendPolarity ?? "neutral"} motion={motion} />
    ) : null;
  return (
    <div className="min-w-0" aria-busy={loading || undefined}>
      <MetricValue
        size={supporting ? "compact" : "headline"}
        title={metric ? formatMetricValue(metric) : undefined}
      >
        {loading ? "…" : (display ?? "—")}
      </MetricValue>
      {layout === "single" ? (
        <div className="mt-3 flex min-w-0 flex-wrap items-end justify-between gap-3">
          {captionNode}
          {badge}
        </div>
      ) : (
        <div className="mt-2 flex min-w-0 flex-col items-start gap-2">
          {captionNode}
          {badge}
        </div>
      )}
    </div>
  );
}

function createIndicatorComponent(manifest: WidgetManifest, periodDetail: boolean) {
  return function MetricIndicator({ data, config }: WidgetProps<MetricIndicatorConfig>) {
    const inEditor = useContext(WidgetEditorContext) !== null;
    // Without a chosen metric the source's first one is shown, so a new tile has real labels at once.
    const primary = useFigure(data, config.metricId, true, config.animateValue === true);
    const secondaryId = config.secondaryMetricId?.trim() || undefined;
    const secondary = useFigure(data, secondaryId, false, config.animateValue === true);
    const thirdId = config.thirdMetricId?.trim() || undefined;
    const third = useFigure(data, thirdId, false, config.animateValue === true);
    const labels = resolveLabels(INDICATOR_LABELS, config);
    const heading = config.title?.trim() || manifest.name;
    const shared = { config, inEditor, labels, periodDetail };

    return (
      <Widget title={heading} intrinsicHeight>
        {primary.error ? (
          // The shared issue state, as in every widget (the host frame shows it too).
          <WidgetIssueState error={primary.error} className="flex-1" />
        ) : (
          <div className="density-metric-padding flex min-h-32 flex-1 flex-col gap-3">
            {thirdId ? (
              <>
                <div className="py-2">
                  <Figure {...shared} figure={primary} caption={config.label} layout="single" />
                </div>
                <div className="mt-1 grid min-w-0 grid-cols-2 gap-4 border-t border-border pt-4">
                  {secondaryId ? (
                    <Figure
                      {...shared}
                      figure={secondary}
                      caption={config.secondaryLabel}
                      layout="pair"
                      showTrend={false}
                      supporting
                    />
                  ) : null}
                  <Figure
                    {...shared}
                    figure={third}
                    caption={config.thirdLabel}
                    layout="pair"
                    showTrend={false}
                    supporting
                  />
                </div>
              </>
            ) : secondaryId ? (
              <div
                // Two figures share the tile: a size down until the tile is wide enough for both
                // at full size, and never broken across lines.
                className="grid min-w-0 grid-cols-2 gap-4 [&_[data-metric-size]]:whitespace-nowrap [&_[data-metric-size]]:text-xl @min-[18rem]/widget:[&_[data-metric-size]]:text-3xl @min-[26rem]/widget:[&_[data-metric-size]]:text-kpi-headline"
              >
                <Figure {...shared} figure={primary} caption={config.label} layout="pair" />
                <Figure
                  {...shared}
                  figure={secondary}
                  caption={config.secondaryLabel}
                  layout="pair"
                />
              </div>
            ) : (
              <Figure {...shared} figure={primary} caption={config.label} layout="single" />
            )}
          </div>
        )}
      </Widget>
    );
  };
}

const FIGURE_KEYS = [
  { metric: "metricId", label: "label" },
  { metric: "secondaryMetricId", label: "secondaryLabel" },
  { metric: "thirdMetricId", label: "thirdLabel" },
] as const;

/** The metric picks and their captions, for indicators of two or three figures. */
function figureProperties(figures: 2 | 3): Record<string, WidgetConfigProperty> {
  const keys = FIGURE_KEYS.slice(0, figures);
  const others = (index: number) =>
    keys.filter((_, other) => other !== index).map((entry) => entry.metric);
  const names = ["Metric", "Second metric", "Third metric"];
  const captions = ["Label below value", "Label below second value", "Label below third value"];
  return Object.fromEntries(
    keys.flatMap((entry, index) => [
      [
        entry.metric,
        {
          type: "string",
          title: names[index],
          "x-ui": {
            category: "data",
            order: 10 + index,
            // Captions follow the choice: a reworded one would describe the previous metric.
            resets: [entry.label],
            options: {
              from: "data",
              request: "metrics",
              path: "metrics",
              ...(index > 0 ? { optional: true } : {}),
              // Two figures never show the same metric (the first one is never repeated).
              ...(figures === 3 || index > 0 ? { distinctFrom: others(index) } : {}),
            },
          },
        } satisfies WidgetConfigProperty,
      ],
      [
        entry.label,
        {
          type: "string",
          title: captions[index],
          "x-ui": { category: "texts", control: "text", advanced: true, order: 10 + index },
        } satisfies WidgetConfigProperty,
      ],
    ]),
  );
}

/** The comparison trend of the primary figure, and which way of change is good news. */
const TREND_CONFIG = (showTrend: boolean) =>
  defineConfigFragment("nexus-kit:metric-trend", 1, {
    showTrend: {
      type: "boolean",
      title: "Change vs previous period",
      description: "Arrow and change against the previous period",
      default: showTrend,
      "x-ui": { category: "display", group: "show", order: 30, summary: "Trend" },
    },
    trendPolarity: {
      type: "string",
      title: "Good trend direction",
      description: "Colours the trend: green when it goes the good way",
      default: "neutral",
      oneOf: [
        { const: "neutral", title: "No colour" },
        { const: "higher-is-better", title: "Higher is better" },
        { const: "lower-is-better", title: "Lower is better" },
      ],
      "x-ui": { category: "display", order: 31, visibleIf: { showTrend: true } },
    },
  });

/**
 * What the editor adjusts from the loaded metrics: a caption shows its metric's
 * name until reworded and only for a chosen metric; the trend is offered only
 * where the source can compute one (or before its metrics are known, or while
 * it is on, so it can be turned off).
 */
function indicatorOverlay(
  config: Readonly<Record<string, JsonValue>>,
  metrics: readonly ReportMetric[],
  comparison: boolean | undefined,
  defaultShowTrend: boolean,
  figures: 2 | 3,
): WidgetConfigOverlay {
  const metricOf = (key: string, first: boolean) => {
    const id = typeof config[key] === "string" ? String(config[key]) : "";
    return id ? metrics.find((metric) => metric.id === id) : first ? metrics[0] : undefined;
  };
  const fallbacks: Record<string, string> = {};
  const hidden: string[] = [];
  FIGURE_KEYS.slice(0, figures).forEach((entry, index) => {
    const metric = metricOf(entry.metric, index === 0);
    if (metric) fallbacks[entry.label] = metric.label;
    else if (index > 0) hidden.push(entry.label);
  });
  const showTrend = typeof config.showTrend === "boolean" ? config.showTrend : defaultShowTrend;
  const supportsTrend =
    comparison === true ||
    metrics.length === 0 ||
    showTrend ||
    metrics.some((metric) => getMetricTrend(metric) !== null);
  if (!supportsTrend) hidden.push("showTrend", "trendPolarity");
  return { fallbacks, hidden };
}

/**
 * An indicator's settings: its metric (two or three figures), their captions,
 * the trend when its source has comparisons, and the value animation.
 */
export function indicatorSettings({
  id,
  comparison,
  showTrend,
  figures = 2,
}: {
  id: string;
  /** `false`: the source has no comparisons (no trend setting); `true`: it always has. */
  comparison?: boolean;
  /** The trend's default. */
  showTrend: boolean;
  figures?: 2 | 3;
}): WidgetSettings {
  return defineWidgetSettings({
    id,
    extends: [ANIMATE_VALUE_CONFIG, ...(comparison === false ? [] : [TREND_CONFIG(showTrend)])],
    properties: figureProperties(figures),
    overlay: ({ config, data }) =>
      indicatorOverlay(
        config,
        unwrap<ReportMetricsData>(data?.metrics)?.metrics ?? [],
        comparison,
        showTrend,
        figures,
      ),
  });
}

/**
 * An indicator widget module on one `report-metrics` source.
 *
 * - With `sources` (usually the one source of a domain), the author never picks a
 *   source: only a metric of that domain.
 * - Without, every source of the contract is offered: the generic `metric-kpi`.
 */
export function createIndicator({
  id,
  name,
  description,
  version = "1.0.0",
  source,
  sources,
  comparison,
  periodDetail = false,
  figures = 2,
}: {
  id: string;
  name: string;
  description: string;
  version?: string;
  source: string;
  sources?: readonly string[];
  comparison?: boolean;
  /**
   * Under each caption, the period the figure covers ("Last 7 complete days
   * (UTC)") when the figure names no moment of its own. For sources whose
   * figures carry no comparison, it is their context.
   */
  periodDetail?: boolean;
  /** How many metrics an author may show: one or two, or up to three. */
  figures?: 2 | 3;
}): WidgetModule<MetricIndicatorConfig> & {
  defaultConfig: MetricIndicatorConfig;
  /** The indicator's settings, for its widget definition. */
  settings: WidgetSettings;
} {
  const manifest: WidgetManifest = {
    id,
    name,
    description,
    version,
    labels: INDICATOR_LABELS,
    dataRequests: {
      metrics: {
        source,
        contract: REPORT_METRICS_CONTRACT,
        configurable: true,
        ...(sources ? { sources } : {}),
      },
    },
  };
  const domainIndicator = sources !== undefined;
  const defaultConfig: MetricIndicatorConfig = {
    title: name,
    showTrend: domainIndicator && comparison !== false,
    trendPolarity: "neutral",
  };
  return {
    manifest,
    Component: createIndicatorComponent(manifest, periodDetail),
    defaultConfig,
    settings: indicatorSettings({
      id,
      comparison,
      showTrend: defaultConfig.showTrend ?? false,
      figures,
    }),
    getDataRequirements(config) {
      const primary = config.metricId?.trim();
      const secondary = config.secondaryMetricId?.trim();
      const third = config.thirdMetricId?.trim();
      return {
        metrics: {
          mode: "select",
          metricIds: [
            ...new Set([primary, secondary, third].filter((id): id is string => Boolean(id))),
          ],
          defaultMetric: !primary,
          includeComparison:
            comparison === false ? false : (config.showTrend ?? defaultConfig.showTrend ?? false),
          fields: ["metrics", "freshness"],
        },
      };
    },
  };
}
